import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadershipManager = () => {
  const [leadership, setLeadership] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLeader, setEditingLeader] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    bio: '',
    image_url: '',
    order: 0
  });

  useEffect(() => {
    fetchLeadership();
  }, []);

  const fetchLeadership = async () => {
    try {
      const response = await axios.get(`${API}/leadership`);
      setLeadership(response.data);
    } catch (error) {
      console.error('Error fetching leadership:', error);
      toast.error('Failed to load leadership');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (leader = null) => {
    if (leader) {
      setEditingLeader(leader);
      setFormData({
        name: leader.name,
        position: leader.position,
        bio: leader.bio,
        image_url: leader.image_url || '',
        order: leader.order
      });
    } else {
      setEditingLeader(null);
      setFormData({
        name: '',
        position: '',
        bio: '',
        image_url: '',
        order: 0
      });
    }
    setDialogOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'order' ? parseInt(value) : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingLeader) {
        await axios.put(`${API}/leadership/${editingLeader.id}`, formData, config);
        toast.success('Leader updated successfully');
      } else {
        await axios.post(`${API}/leadership`, formData, config);
        toast.success('Leader added successfully');
      }

      setDialogOpen(false);
      fetchLeadership();
    } catch (error) {
      console.error('Error saving leader:', error);
      toast.error('Failed to save leader');
    }
  };

  const handleDelete = async (leaderId) => {
    if (!window.confirm('Are you sure you want to delete this leader?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/leadership/${leaderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Leader deleted successfully');
      fetchLeadership();
    } catch (error) {
      console.error('Error deleting leader:', error);
      toast.error('Failed to delete leader');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Leadership Management</h2>
          <p className="text-gray-600">Manage organization leadership profiles</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          data-testid="add-leader-btn"
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
        >
          <Plus className="mr-2" size={20} />
          Add Leader
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {leadership.map((leader) => (
            <div key={leader.id} data-testid={`leader-item-${leader.id}`} className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{leader.name}</h3>
                  <p className="text-orange-500 font-semibold mb-3">{leader.position}</p>
                  <p className="text-sm text-gray-600 mb-2">{leader.bio}</p>
                  <p className="text-xs text-gray-500">Display Order: {leader.order}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleOpenDialog(leader)}
                    data-testid={`edit-leader-${leader.id}`}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(leader.id)}
                    data-testid={`delete-leader-${leader.id}`}
                    className="text-red-600 hover:text-red-800 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingLeader ? 'Edit Leader' : 'Add Leader'}</DialogTitle>
            <DialogDescription>
              {editingLeader ? 'Update leader information' : 'Add a new leader profile'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} data-testid="leader-form" className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                name="name"
                data-testid="input-name"
                value={formData.name}
                onChange={handleChange}
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="position">Position *</Label>
              <Input
                id="position"
                name="position"
                data-testid="input-position"
                value={formData.position}
                onChange={handleChange}
                required
                className="mt-1"
                placeholder="e.g., President, Secretary General"
              />
            </div>

            <div>
              <Label htmlFor="bio">Bio *</Label>
              <Textarea
                id="bio"
                name="bio"
                data-testid="input-bio"
                value={formData.bio}
                onChange={handleChange}
                required
                rows={4}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="image_url">Image URL</Label>
              <Input
                id="image_url"
                name="image_url"
                data-testid="input-image-url"
                value={formData.image_url}
                onChange={handleChange}
                className="mt-1"
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div>
              <Label htmlFor="order">Display Order</Label>
              <Input
                id="order"
                name="order"
                type="number"
                data-testid="input-order"
                value={formData.order}
                onChange={handleChange}
                className="mt-1"
                placeholder="0"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                data-testid="submit-leader-btn"
                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              >
                {editingLeader ? 'Update Leader' : 'Add Leader'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadershipManager;